// src/components/ReservationTable.jsx
import React, { useState } from 'react';
import { 
  Check, 
  X, 
  Loader2, 
  Mail, 
  Trash2, 
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Edit2,
  CheckCircle,
  XCircle,
  Clock,
  User,
  Calendar,
  Users,
  MessageSquare,
  MoreHorizontal,
  Copy,
  Phone,
  CalendarDays
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';

export default function ReservationTable({
  reservations = [],
  onApprove,
  onReject,
  onDelete,
  onSendEmail,
  onUpdateTableNumber,
  processingId,
  emailStatus = {}
}) {
  const [editingTable, setEditingTable] = useState(null);
  const [tableNumberInput, setTableNumberInput] = useState('');
  const [expandedRequests, setExpandedRequests] = useState({});
  const [expandedEmails, setExpandedEmails] = useState({});
  const [copiedEmail, setCopiedEmail] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [mobileExpandedRow, setMobileExpandedRow] = useState(null);
  const itemsPerPage = 10;

  // Toggle expanded special request
  const toggleExpandedRequest = (id) => {
    setExpandedRequests(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Toggle expanded email
  const toggleExpandedEmail = (id) => {
    setExpandedEmails(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Toggle mobile expanded row
  const toggleMobileRow = (id) => {
    setMobileExpandedRow(prev => prev === id ? null : id);
  };

  // Copy email to clipboard
  const copyEmailToClipboard = (email) => {
    if (!email) return;
    navigator.clipboard.writeText(email);
    setCopiedEmail(email);
    setTimeout(() => setCopiedEmail(null), 2000);
  };

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sort reservations
  const sortedReservations = [...reservations].sort((a, b) => {
    let aVal, bVal;
    
    switch(sortField) {
      case 'customer_name':
        aVal = (a.customer_name || '').toLowerCase();
        bVal = (b.customer_name || '').toLowerCase();
        break;
      case 'date':
        aVal = a.date || '';
        bVal = b.date || '';
        break;
      case 'time':
        aVal = a.time || '';
        bVal = b.time || '';
        break;
      case 'guests':
        aVal = a.guests || 0;
        bVal = b.guests || 0;
        break;
      case 'status':
        aVal = a.status || '';
        bVal = b.status || '';
        break;
      case 'created_at':
        aVal = a.created_at || '';
        bVal = b.created_at || '';
        break;
      default:
        aVal = a[sortField] || '';
        bVal = b[sortField] || '';
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Paginate
  const totalPages = Math.ceil(sortedReservations.length / itemsPerPage);
  const paginatedReservations = sortedReservations.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset to first page when reservations change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [reservations.length]);

  // Get status color
  const getStatusColor = (status) => {
    switch(status) {
      case 'approved': return 'text-emerald-600 bg-emerald-50 border-emerald-200';
      case 'rejected': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-amber-600 bg-amber-50 border-amber-200';
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch(status) {
      case 'approved': return <CheckCircle className="w-4 h-4" />;
      case 'rejected': return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(parseISO(dateString), 'dd/MM/yyyy');
    } catch {
      return dateString;
    }
  };

  // Format date with time
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(parseISO(dateString), 'dd/MM/yyyy HH:mm');
    } catch {
      return dateString;
    }
  };

  // Truncate text with configurable length
  const truncateText = (text, length = 60) => {
    if (!text) return '';
    if (text.length <= length) return text;
    return text.slice(0, length) + '...';
  };

  // Sortable header
  const SortableHeader = ({ field, children }) => {
    const isActive = sortField === field;
    return (
      <div 
        className="flex items-center gap-1 cursor-pointer hover:text-[#2D2D2D] transition-colors"
        onClick={() => handleSort(field)}
      >
        {children}
        {isActive ? (
          sortDirection === 'asc' ? 
            <ChevronUp className="w-3 h-3" /> : 
            <ChevronDown className="w-3 h-3" />
        ) : (
          <ChevronDown className="w-3 h-3 opacity-30" />
        )}
      </div>
    );
  };

  // Mobile Reservation Card - Mobile Optimized
  const MobileReservationCard = ({ reservation }) => {
    const isProcessing = processingId === reservation.id;
    const emailStatusData = emailStatus[reservation.id];
    const isExpanded = mobileExpandedRow === reservation.id;
    const isRequestExpanded = expandedRequests[reservation.id] || false;
    const isEmailExpanded = expandedEmails[reservation.id] || false;
    const specialRequest = reservation.special_requests || '';
    const email = reservation.email || '';
    const shouldTruncateRequest = specialRequest.length > 60;
    const shouldTruncateEmail = email.length > 30;
    const displayRequest = isRequestExpanded || !shouldTruncateRequest 
      ? specialRequest 
      : truncateText(specialRequest, 60);
    const displayEmail = isEmailExpanded || !shouldTruncateEmail 
      ? email 
      : truncateText(email, 30);

    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white border border-[#E5E5E5] rounded-lg mb-3 overflow-hidden"
      >
        {/* Main Row - Always Visible */}
        <div 
          className="p-3 cursor-pointer"
          onClick={() => toggleMobileRow(reservation.id)}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-[#8B2635]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-[#8B2635]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-[#2D2D2D] truncate">
                    {reservation.customer_name || 'N/A'}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-[#6B6B6B]">
                    <span>{formatDate(reservation.date)}</span>
                    <span>•</span>
                    <span>{reservation.time || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 ml-2 flex-shrink-0">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(reservation.status)}`}>
                {getStatusIcon(reservation.status)}
                <span className="hidden xs:inline">{reservation.status || 'pending'}</span>
              </span>
              <button className="p-1 text-[#6B6B6B]">
                <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Expanded Details */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="border-t border-[#E5E5E5]"
            >
              <div className="p-3 space-y-3">
                {/* Contact Info */}
                <div className="space-y-1.5">
                  {email && (
                    <div className="flex items-center gap-2 bg-[#F9F9F9] p-2 rounded-lg">
                      <Mail className="w-3.5 h-3.5 text-[#6B6B6B] flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-[#2D2D2D] break-all">
                          {displayEmail}
                        </p>
                        {shouldTruncateEmail && (
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleExpandedEmail(reservation.id); }}
                            className="text-xs text-[#8B2635] hover:underline font-medium"
                          >
                            {isEmailExpanded ? 'Show less' : 'Show more'}
                          </button>
                        )}
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); copyEmailToClipboard(email); }}
                        className="p-1 text-[#6B6B6B] hover:text-[#8B2635] hover:bg-[#8B2635]/10 rounded transition-colors flex-shrink-0"
                      >
                        {copiedEmail === email ? (
                          <Check className="w-3.5 h-3.5 text-emerald-600" />
                        ) : (
                          <Copy className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </div>
                  )}
                  
                  {reservation.phone && (
                    <div className="flex items-center gap-2 text-sm text-[#6B6B6B]">
                      <Phone className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="break-words">{reservation.phone}</span>
                    </div>
                  )}
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-[#F9F9F9] p-2 rounded-lg">
                    <p className="text-[10px] text-[#6B6B6B] uppercase font-medium">Date</p>
                    <p className="text-sm font-medium text-[#2D2D2D]">{formatDate(reservation.date)}</p>
                  </div>
                  <div className="bg-[#F9F9F9] p-2 rounded-lg">
                    <p className="text-[10px] text-[#6B6B6B] uppercase font-medium">Time</p>
                    <p className="text-sm font-medium text-[#2D2D2D]">{reservation.time || 'N/A'}</p>
                  </div>
                  <div className="bg-[#F9F9F9] p-2 rounded-lg">
                    <p className="text-[10px] text-[#6B6B6B] uppercase font-medium">Guests</p>
                    <p className="text-sm font-medium text-[#2D2D2D]">{reservation.guests || 'N/A'}</p>
                  </div>
                  <div className="bg-[#F9F9F9] p-2 rounded-lg">
                    <p className="text-[10px] text-[#6B6B6B] uppercase font-medium">Table</p>
                    <div className="flex items-center gap-1">
                      {editingTable === reservation.id ? (
                        <div className="flex items-center gap-1">
                          <input
                            type="text"
                            value={tableNumberInput}
                            onChange={(e) => setTableNumberInput(e.target.value)}
                            className="w-12 px-1.5 py-0.5 text-sm border border-[#E5E5E5] rounded focus:outline-none focus:ring-2 focus:ring-[#8B2635]/40"
                            autoFocus
                            placeholder="#"
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (tableNumberInput.trim()) {
                                onUpdateTableNumber(reservation.id, tableNumberInput.trim());
                                setEditingTable(null);
                                setTableNumberInput('');
                              }
                            }}
                            className="p-0.5 text-emerald-600 hover:bg-emerald-50 rounded"
                          >
                            <Check className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingTable(null);
                              setTableNumberInput('');
                            }}
                            className="p-0.5 text-red-600 hover:bg-red-50 rounded"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <span className="text-sm font-medium text-[#2D2D2D]">
                            {reservation.table_number || '—'}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingTable(reservation.id);
                              setTableNumberInput(reservation.table_number || '');
                            }}
                            className="p-0.5 text-[#6B6B6B] hover:text-[#8B2635] hover:bg-[#8B2635]/10 rounded transition-colors"
                          >
                            <Edit2 className="w-3 h-3" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Special Requests */}
                {specialRequest && (
                  <div className="bg-[#F9F9F9] p-2 rounded-lg">
                    <div className="flex items-start gap-1.5">
                      <MessageSquare className="w-3.5 h-3.5 text-[#6B6B6B] flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] text-[#6B6B6B] uppercase font-medium">Special Request</p>
                        <p className="text-sm text-[#2D2D2D] break-words whitespace-pre-wrap">
                          {displayRequest}
                        </p>
                        {shouldTruncateRequest && (
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleExpandedRequest(reservation.id); }}
                            className="text-xs text-[#8B2635] hover:underline font-medium"
                          >
                            {isRequestExpanded ? 'Show less' : 'Show more'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Actions - Mobile Optimized with smaller text on very small screens */}
                <div className="flex items-center gap-1 pt-1 border-t border-[#E5E5E5]">
                  {reservation.status !== 'approved' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onApprove(reservation); }}
                      disabled={isProcessing}
                      className="flex-1 py-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-1 text-[11px] xs:text-sm"
                    >
                      {isProcessing ? (
                        <Loader2 className="w-3 h-3 xs:w-4 xs:h-4 animate-spin" />
                      ) : (
                        <Check className="w-3 h-3 xs:w-4 xs:h-4" />
                      )}
                      <span className="hidden xs:inline">Approve</span>
                    </button>
                  )}

                  {reservation.status !== 'rejected' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onReject(reservation); }}
                      disabled={isProcessing}
                      className="flex-1 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-1 text-[11px] xs:text-sm"
                    >
                      <X className="w-3 h-3 xs:w-4 xs:h-4" />
                      <span className="hidden xs:inline">Reject</span>
                    </button>
                  )}

                  {reservation.email && reservation.email.trim() !== '' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onSendEmail(reservation); }}
                      disabled={isProcessing}
                      className="flex-1 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-1 text-[11px] xs:text-sm relative"
                    >
                      {emailStatusData?.loading ? (
                        <Loader2 className="w-3 h-3 xs:w-4 xs:h-4 animate-spin" />
                      ) : emailStatusData?.success ? (
                        <Check className="w-3 h-3 xs:w-4 xs:h-4 text-emerald-600" />
                      ) : (
                        <Mail className="w-3 h-3 xs:w-4 xs:h-4" />
                      )}
                      <span className="hidden xs:inline">Email</span>
                    </button>
                  )}

                  <button
                    onClick={(e) => { e.stopPropagation(); onDelete(reservation); }}
                    disabled={isProcessing}
                    className="flex-1 py-2 text-[#6B6B6B] hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-1 text-[11px] xs:text-sm"
                  >
                    <Trash2 className="w-3 h-3 xs:w-4 xs:h-4" />
                    <span className="hidden xs:inline">Delete</span>
                  </button>
                </div>

                {/* Email Status Message */}
                {emailStatusData?.message && (
                  <div className="text-xs text-[#6B6B6B] text-center bg-[#F9F9F9] p-1.5 rounded">
                    {emailStatusData.message}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  return (
    <div className="w-full">
      {/* Desktop Table View - Hidden on Mobile */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full divide-y divide-[#E5E5E5] min-w-[900px]">
          <thead className="bg-[#F9F9F9]">
            <tr>
              <th className="w-[12%] px-3 py-3 text-left text-xs font-medium text-[#6B6B6B] uppercase tracking-wider">
                <SortableHeader field="customer_name">Customer</SortableHeader>
              </th>
              <th className="w-[18%] px-3 py-3 text-left text-xs font-medium text-[#6B6B6B] uppercase tracking-wider">
                <SortableHeader field="contact">Contact</SortableHeader>
              </th>
              <th className="w-[8%] px-3 py-3 text-left text-xs font-medium text-[#6B6B6B] uppercase tracking-wider">
                <SortableHeader field="date">Date</SortableHeader>
              </th>
              <th className="w-[8%] px-3 py-3 text-left text-xs font-medium text-[#6B6B6B] uppercase tracking-wider">
                <SortableHeader field="time">Time</SortableHeader>
              </th>
              <th className="w-[7%] px-3 py-3 text-left text-xs font-medium text-[#6B6B6B] uppercase tracking-wider">
                <SortableHeader field="guests">Guests</SortableHeader>
              </th>
              <th className="w-[8%] px-3 py-3 text-left text-xs font-medium text-[#6B6B6B] uppercase tracking-wider">
                <SortableHeader field="table">Table</SortableHeader>
              </th>
              <th className="w-[20%] px-3 py-3 text-left text-xs font-medium text-[#6B6B6B] uppercase tracking-wider">
                <SortableHeader field="special">Special Requests</SortableHeader>
              </th>
              <th className="w-[12%] px-3 py-3 text-left text-xs font-medium text-[#6B6B6B] uppercase tracking-wider">
                <SortableHeader field="status">Status</SortableHeader>
              </th>
              <th className="w-[7%] px-3 py-3 text-right text-xs font-medium text-[#6B6B6B] uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-[#E5E5E5]">
            {paginatedReservations.length === 0 ? (
              <tr>
                <td colSpan="9" className="px-4 py-12 text-center text-[#6B6B6B]">
                  <div className="flex flex-col items-center gap-2">
                    <Calendar className="w-8 h-8 text-[#CCCCCC]" />
                    <p>No reservations found</p>
                    <p className="text-sm">Try adjusting your filters</p>
                  </div>
                </td>
              </tr>
            ) : (
              paginatedReservations.map((reservation) => {
                const isProcessing = processingId === reservation.id;
                const emailStatusData = emailStatus[reservation.id];
                const isRequestExpanded = expandedRequests[reservation.id] || false;
                const isEmailExpanded = expandedEmails[reservation.id] || false;
                const specialRequest = reservation.special_requests || '';
                const email = reservation.email || '';
                const shouldTruncateRequest = specialRequest.length > 80;
                const shouldTruncateEmail = email.length > 35;
                const displayRequest = isRequestExpanded || !shouldTruncateRequest 
                  ? specialRequest 
                  : truncateText(specialRequest, 80);
                const displayEmail = isEmailExpanded || !shouldTruncateEmail 
                  ? email 
                  : truncateText(email, 35);

                return (
                  <motion.tr 
                    key={reservation.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="hover:bg-[#FDF8F0] transition-colors"
                  >
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-[#8B2635]/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4 text-[#8B2635]" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-[#2D2D2D] truncate">
                            {reservation.customer_name || 'N/A'}
                          </p>
                          {reservation.date_of_birth && (
                            <p className="text-xs text-[#6B6B6B] truncate">
                              🎂 {formatDate(reservation.date_of_birth)}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="space-y-1">
                        {email && (
                          <div className="flex items-center gap-1 group">
                            <Mail className="w-3 h-3 text-[#6B6B6B] flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-[#2D2D2D] break-all">
                                {displayEmail}
                              </p>
                              {shouldTruncateEmail && (
                                <button
                                  onClick={() => toggleExpandedEmail(reservation.id)}
                                  className="text-xs text-[#8B2635] hover:underline font-medium"
                                >
                                  {isEmailExpanded ? 'Show less' : 'Show more'}
                                </button>
                              )}
                            </div>
                            <button
                              onClick={() => copyEmailToClipboard(email)}
                              className="p-1 text-[#6B6B6B] hover:text-[#8B2635] hover:bg-[#8B2635]/10 rounded transition-colors flex-shrink-0 opacity-0 group-hover:opacity-100"
                              title="Copy email"
                            >
                              {copiedEmail === email ? (
                                <Check className="w-3 h-3 text-emerald-600" />
                              ) : (
                                <Copy className="w-3 h-3" />
                              )}
                            </button>
                          </div>
                        )}
                        
                        {reservation.phone && (
                          <div className="flex items-center gap-1">
                            <span className="text-sm text-[#6B6B6B] truncate">
                              {reservation.phone}
                            </span>
                          </div>
                        )}
                        
                        {!email && !reservation.phone && (
                          <span className="text-sm text-[#6B6B6B]">—</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3 text-[#6B6B6B] flex-shrink-0" />
                        <span className="text-sm text-[#2D2D2D] whitespace-nowrap">
                          {formatDate(reservation.date)}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <span className="text-sm text-[#2D2D2D] whitespace-nowrap">
                        {reservation.time || 'N/A'}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1">
                        <Users className="w-3 h-3 text-[#6B6B6B] flex-shrink-0" />
                        <span className="text-sm text-[#2D2D2D]">
                          {reservation.guests || 'N/A'}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1">
                        {editingTable === reservation.id ? (
                          <div className="flex items-center gap-1">
                            <input
                              type="text"
                              value={tableNumberInput}
                              onChange={(e) => setTableNumberInput(e.target.value)}
                              className="w-14 px-2 py-1 text-sm border border-[#E5E5E5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B2635]/40"
                              autoFocus
                              placeholder="#"
                            />
                            <button
                              onClick={() => {
                                if (tableNumberInput.trim()) {
                                  onUpdateTableNumber(reservation.id, tableNumberInput.trim());
                                  setEditingTable(null);
                                  setTableNumberInput('');
                                }
                              }}
                              className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setEditingTable(null);
                                setTableNumberInput('');
                              }}
                              className="p-1 text-red-600 hover:bg-red-50 rounded"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <>
                            <span className="text-sm font-medium text-[#2D2D2D]">
                              {reservation.table_number || '—'}
                            </span>
                            <button
                              onClick={() => {
                                setEditingTable(reservation.id);
                                setTableNumberInput(reservation.table_number || '');
                              }}
                              className="p-1 text-[#6B6B6B] hover:text-[#8B2635] hover:bg-[#8B2635]/10 rounded transition-colors"
                            >
                              <Edit2 className="w-3 h-3" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-start gap-1">
                        <MessageSquare className="w-3 h-3 text-[#6B6B6B] flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          {specialRequest ? (
                            <>
                              <p className="text-sm text-[#2D2D2D] break-words whitespace-pre-wrap">
                                {displayRequest}
                              </p>
                              {shouldTruncateRequest && (
                                <button
                                  onClick={() => toggleExpandedRequest(reservation.id)}
                                  className="text-xs text-[#8B2635] hover:underline font-medium"
                                >
                                  {isRequestExpanded ? 'Show less' : 'Show more'}
                                </button>
                              )}
                            </>
                          ) : (
                            <span className="text-sm text-[#6B6B6B]">—</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-wrap items-center gap-1">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(reservation.status)}`}>
                          {getStatusIcon(reservation.status)}
                          {reservation.status || 'pending'}
                        </span>
                        {reservation.status === 'approved' && reservation.approved_at && (
                          <span className="text-[10px] text-[#6B6B6B] whitespace-nowrap">
                            {formatDateTime(reservation.approved_at)}
                          </span>
                        )}
                        {reservation.status === 'rejected' && reservation.rejected_at && (
                          <span className="text-[10px] text-[#6B6B6B] whitespace-nowrap">
                            {formatDateTime(reservation.rejected_at)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-end gap-0.5">
                        {/* Approve Button */}
                        {reservation.status !== 'approved' && (
                          <button
                            onClick={() => onApprove(reservation)}
                            disabled={isProcessing}
                            className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors disabled:opacity-50"
                            title="Approve"
                          >
                            {isProcessing ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Check className="w-4 h-4" />
                            )}
                          </button>
                        )}

                        {/* Reject Button */}
                        {reservation.status !== 'rejected' && (
                          <button
                            onClick={() => onReject(reservation)}
                            disabled={isProcessing}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                            title="Reject"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}

                        {/* Email Button */}
                        {reservation.email && reservation.email.trim() !== '' && (
                          <button
                            onClick={() => onSendEmail(reservation)}
                            disabled={isProcessing}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 relative"
                            title="Send Email"
                          >
                            {emailStatusData?.loading ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : emailStatusData?.success ? (
                              <Check className="w-4 h-4 text-emerald-600" />
                            ) : emailStatusData?.success === false ? (
                              <Mail className="w-4 h-4 text-red-600" />
                            ) : (
                              <Mail className="w-4 h-4" />
                            )}
                          </button>
                        )}

                        {/* Delete Button */}
                        <button
                          onClick={() => onDelete(reservation)}
                          disabled={isProcessing}
                          className="p-1.5 text-[#6B6B6B] hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>

                        {/* Email Status Tooltip */}
                        {emailStatusData?.message && (
                          <div className="relative group">
                            <MoreHorizontal className="w-3 h-3 text-[#6B6B6B] cursor-help" />
                            <div className="absolute bottom-full right-0 mb-1 px-2 py-1 bg-[#2D2D2D] text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                              {emailStatusData.message}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View - Visible on Mobile/Tablet */}
      <div className="lg:hidden p-3">
        {paginatedReservations.length === 0 ? (
          <div className="text-center py-12 text-[#6B6B6B]">
            <div className="flex flex-col items-center gap-2">
              <Calendar className="w-8 h-8 text-[#CCCCCC]" />
              <p>No reservations found</p>
              <p className="text-sm">Try adjusting your filters</p>
            </div>
          </div>
        ) : (
          paginatedReservations.map((reservation) => (
            <MobileReservationCard 
              key={reservation.id} 
              reservation={reservation} 
            />
          ))
        )}
      </div>

      {/* Pagination - Mobile Friendly */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-3 sm:px-4 py-3 border-t border-[#E5E5E5]">
          <div className="text-xs sm:text-sm text-[#6B6B6B] order-2 sm:order-1">
            Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
            {Math.min(currentPage * itemsPerPage, sortedReservations.length)} of{' '}
            {sortedReservations.length}
          </div>
          <div className="flex items-center gap-1 sm:gap-2 order-1 sm:order-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="p-1.5 sm:p-2 border border-[#E5E5E5] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#F9F9F9] transition-colors min-w-[36px] min-h-[36px] flex items-center justify-center"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            
            {/* Page Numbers - Mobile friendly with touch targets */}
            <div className="flex items-center gap-0.5 sm:gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                if (pageNum < 1 || pageNum > totalPages) return null;
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`min-w-[32px] min-h-[32px] sm:w-8 sm:h-8 text-xs sm:text-sm rounded-lg transition-colors flex items-center justify-center ${
                      currentPage === pageNum
                        ? 'bg-[#8B2635] text-white'
                        : 'text-[#2D2D2D] hover:bg-[#F9F9F9]'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="p-1.5 sm:p-2 border border-[#E5E5E5] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#F9F9F9] transition-colors min-w-[36px] min-h-[36px] flex items-center justify-center"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}